import React from 'react';
import { Link } from 'react-router-dom';
import { Truck, MapPin, Map, ShieldCheck, Utensils, ShoppingBasket, Store, GlassWater, Hotel, PartyPopper, CheckCircle2, ArrowRight, Phone } from 'lucide-react';
import { SEGMENTS, TIERS, PROCESS_STEPS, SERVICE_AREAS, WHY_US, FAQ_ITEMS, BRAND, STOCK } from '@/lib/constants';
import Hero from '@/components/Hero';
import SectionHeader from '@/components/SectionHeader';
import Reveal from '@/components/Reveal';
import SquareButton from '@/components/SquareButton';
import WholesaleInquiryForm from '@/components/forms/WholesaleInquiryForm';
import Seo from '@/components/Seo';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const SEGMENT_ICONS = { Utensils, ShoppingBasket, Store, GlassWater, Hotel, PartyPopper, Truck };
const AREA_ICONS = { MapPin, Map, Truck };

export default function Wholesale() {
  return (
    <div data-testid="wholesale-page">
      <Seo
        title="Wholesale Florida Mangoes & Produce Distribution"
        description="Bulk Florida-grown mangoes for retailers, restaurants, juice bars, hotels, event vendors, and produce distributors. Request a custom wholesale quote."
        keywords="wholesale mangoes, bulk Florida mangoes, mango distribution, produce distributor, Florida produce wholesale, restaurant mango supply"
        ogImage={STOCK.mango_crate}
      />

      <Hero
        testId="wholesale-hero"
        eyebrow="Wholesale & Produce Distribution"
        title={<>Florida mangoes <br className="hidden sm:block" /> in <span className="text-[hsl(var(--c-mango-yellow))]">bulk</span>.</>}
        subtitle="Florida-grown mangoes sourced directly from local growers — with wholesale distribution for retailers, restaurants, distributors, produce buyers, and bulk purchasers throughout Florida and across the United States."
        block="teal"
        bgImage={STOCK.mango_crate}
        rightImage={STOCK.mangoes_pile}
        imageBlock="orange"
        rightAccent="yellow"
        leftAccent="red"
        ctas={[
          { label: 'Request Pricing', href: '#wholesale-inquiry-form', variant: 'yellow', testId: 'wholesale-hero-request-pricing' },
          { label: 'Become a Partner', href: '#wholesale-inquiry-form', variant: 'orange', testId: 'wholesale-hero-become-partner' },
        ]}
      />

      {/* Stats strip */}
      <section className="block-yellow border-b-2 border-[hsl(var(--c-ink))]">
        <div className="mx-auto max-w-[1400px] grid grid-cols-2 lg:grid-cols-4 border-l-0 border-r-0">
          {[
            { value: '120+', label: 'Grower network' },
            { value: '3', label: 'South Florida counties' },
            { value: '6', label: 'Wholesale tiers' },
            { value: 'May–Aug', label: 'Peak harvest months' },
          ].map((s, i) => (
            <Reveal key={s.label} delay={i * 0.04}>
              <div className={`p-6 lg:p-8 h-full border-[hsl(var(--c-ink))] ${i % 2 === 0 ? 'border-r-2' : ''} ${i < 2 ? 'border-b-2 lg:border-b-0' : ''} ${i === 2 ? 'lg:border-r-2' : ''}`}>
                <div className="font-heading text-4xl lg:text-5xl font-extrabold text-[hsl(var(--c-ink))]">{s.value}</div>
                <div className="mt-1 text-xs uppercase tracking-wider font-bold text-[hsl(var(--c-ink))]/70">{s.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="wholesale-who-we-serve" className="block-cream border-t-2 border-[hsl(var(--c-ink))]">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-10 lg:px-16 py-20 lg:py-28">
          <Reveal>
            <SectionHeader
              eyebrow="Who We Serve"
              size="xl"
              title={<>Built for the businesses <br className="hidden sm:block" /> that move Florida produce.</>}
              subtitle="From neighborhood juice bars to national produce distributors, we tailor mango supply to your business needs and harvest windows."
            />
          </Reveal>

          <div className="mt-12 sm:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border-2 border-[hsl(var(--c-ink))]">
            {SEGMENTS.map((s, i) => {
              const Icon = SEGMENT_ICONS[s.icon] || Truck;
              const accents = ['bg-[hsl(var(--c-mango-orange))]', 'bg-[hsl(var(--c-leaf))]', 'bg-[hsl(var(--c-mango-red))]', 'bg-[hsl(var(--c-teal))]', 'bg-[hsl(var(--c-mango-yellow))]', 'bg-[hsl(var(--c-mango-orange))]', 'bg-[hsl(var(--c-leaf))]'];
              return (
                <Reveal key={s.id} delay={i * 0.04}>
                  <div className="group relative bg-white h-full border-r-2 border-b-2 border-[hsl(var(--c-ink))] last:border-r-0 sm:[&:nth-child(2n)]:border-r-0 lg:[&:nth-child(2n)]:border-r-2 lg:[&:nth-child(3n)]:border-r-0 lift-on-hover overflow-hidden" data-testid={`wholesale-segment-${s.id}`}>
                    {s.image && (
                      <div className="relative aspect-[16/9] w-full overflow-hidden border-b-2 border-[hsl(var(--c-ink))]">
                        <img
                          src={s.image}
                          alt=""
                          aria-hidden="true"
                          loading="lazy"
                          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
                      </div>
                    )}
                    <div className="p-7 sm:p-8">
                      <div className={`inline-flex h-12 w-12 items-center justify-center ${accents[i]} border-2 border-[hsl(var(--c-ink))] text-white`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <h3 className="mt-5 font-heading text-2xl font-extrabold">{s.title}</h3>
                      <p className="mt-2 text-sm text-[hsl(var(--c-ink-soft))] leading-relaxed">{s.blurb}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHOLESALE FEATURE STRIP */}
      <section aria-hidden="true" className="relative h-[280px] sm:h-[340px] lg:h-[420px] border-t-2 border-b-2 border-[hsl(var(--c-ink))] overflow-hidden">
        <img src={STOCK.mangoes_pile} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--c-ink))]/65 via-[hsl(var(--c-ink))]/30 to-transparent" />
        <div className="absolute inset-0 grain" />
        <div className="relative mx-auto max-w-[1400px] h-full px-5 sm:px-10 lg:px-16 flex items-center">
          <div className="max-w-2xl">
            <div className="eyebrow text-white/90">Direct From The Source</div>
            <h3 className="mt-3 font-heading display-md font-extrabold text-white leading-[0.98]">
              Tens of thousands of pounds, <span className="text-[hsl(var(--c-mango-yellow))]">hand-harvested</span> each season.
            </h3>
          </div>
        </div>
        <div className="absolute top-0 left-0 right-0 h-[5px] bg-[hsl(var(--c-mango-yellow))]" />
        <div className="absolute bottom-0 left-0 right-0 h-[5px] bg-[hsl(var(--c-mango-orange))]" />
      </section>

      <section data-testid="wholesale-order-tiers" className="block-ink border-t-2 border-[hsl(var(--c-ink))]">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-10 lg:px-16 py-20 lg:py-28">
          <Reveal>
            <SectionHeader
              tone="cream"
              eyebrow="Wholesale Tiers"
              size="xl"
              title="Order levels & quantities."
              subtitle="Availability varies by season, variety, and harvest schedules. We build custom programs for recurring buyers and distributors."
            />
          </Reveal>
          <div data-testid="wholesale-order-tiers-table" className="mt-12 border-2 border-[hsl(var(--c-cream))]/30 overflow-x-auto no-scrollbar">
            <div className="min-w-[680px]">
              <div className="grid grid-cols-12 bg-[hsl(var(--c-mango-yellow))] text-[hsl(var(--c-ink))] border-b-2 border-[hsl(var(--c-cream))]/30">
                <div className="col-span-5 p-5 text-[11px] uppercase tracking-[0.18em] font-bold border-r-2 border-[hsl(var(--c-ink))]/25">Order Level</div>
                <div className="col-span-3 p-5 text-[11px] uppercase tracking-[0.18em] font-bold border-r-2 border-[hsl(var(--c-ink))]/25">Quantity</div>
                <div className="col-span-4 p-5 text-[11px] uppercase tracking-[0.18em] font-bold">Typical For</div>
              </div>
              {TIERS.map((t, i) => (
                <div key={t.name} data-testid={`wholesale-tier-row-${t.name.toLowerCase().replace(/[^a-z]+/g, '-')}`} className={`grid grid-cols-12 text-[hsl(var(--c-cream))] ${i < TIERS.length - 1 ? 'border-b-2 border-[hsl(var(--c-cream))]/20' : ''} hover:bg-white/5 btn-transition`}>
                  <div className="col-span-5 p-5 font-heading text-base sm:text-lg font-bold border-r-2 border-[hsl(var(--c-cream))]/20">{t.name}</div>
                  <div className="col-span-3 p-5 font-mono text-xs sm:text-sm text-[hsl(var(--c-mango-yellow))] border-r-2 border-[hsl(var(--c-cream))]/20">{t.qty}</div>
                  <div className="col-span-4 p-5 text-xs sm:text-sm text-[hsl(var(--c-cream))]/80">{t.desc}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-4 text-xs text-[hsl(var(--c-cream))]/60 uppercase tracking-wider">Note: Availability depends on season, variety, and harvest schedules.</div>
        </div>
      </section>

      <section className="block-cream-deep border-t-2 border-[hsl(var(--c-ink))]">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-10 lg:px-16 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
            <Reveal className="lg:col-span-5" variant="left">
              <SectionHeader
                eyebrow="Why Partner With Us"
                size="xl"
                title={<>Reliable, seasonal, <br className="hidden sm:block" /> Florida-grown supply.</>}
                subtitle="We connect direct grower relationships with the logistics buyers need — from a single local restaurant to multi-state distribution programs."
              />
            </Reveal>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-0 border-2 border-[hsl(var(--c-ink))]">
              {WHY_US.map((w, i) => (
                <Reveal key={w} delay={i * 0.04} variant="right">
                  <div className="flex items-start gap-3 bg-white p-5 h-full border-r-2 border-b-2 border-[hsl(var(--c-ink))] last:border-r-0 sm:[&:nth-child(2n)]:border-r-0 lift-on-hover" data-testid={`wholesale-why-${w.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                    <CheckCircle2 className="h-5 w-5 text-[hsl(var(--c-leaf))] shrink-0 mt-0.5" />
                    <div className="text-sm font-semibold">{w}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section data-testid="wholesale-process-timeline" className="block-leaf border-t-2 border-[hsl(var(--c-ink))]">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-10 lg:px-16 py-20 lg:py-28">
          <Reveal>
            <SectionHeader
              tone="cream"
              eyebrow="How It Works"
              size="xl"
              title="A simple 4-step process."
              subtitle="From the first inquiry to fulfillment, we manage availability, logistics, and grower coordination so you can focus on your business."
            />
          </Reveal>
          <div className="mt-12 sm:mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border-2 border-white/30">
            {PROCESS_STEPS.map((s, i) => (
              <Reveal key={s.step} delay={i * 0.06}>
                <div className="bg-white p-7 sm:p-8 text-[hsl(var(--c-ink))] h-full border-r-2 border-b-2 border-[hsl(var(--c-ink))] last:border-r-0 md:[&:nth-child(2n)]:border-r-0 lg:[&:nth-child(2n)]:border-r-2 lg:[&:nth-child(4n)]:border-r-0 md:[&:nth-last-child(-n+2)]:border-b-0 lg:[&:nth-last-child(-n+4)]:border-b-0 lift-on-hover" data-testid={`wholesale-process-step-${s.step}`}>
                  <div className="inline-flex h-14 w-14 items-center justify-center bg-[hsl(var(--c-ink))] text-[hsl(var(--c-mango-yellow))] font-heading text-2xl font-extrabold border-2 border-[hsl(var(--c-ink))]">{s.step}</div>
                  <h3 className="mt-5 font-heading text-xl font-extrabold leading-tight">{s.title}</h3>
                  <p className="mt-2 text-sm text-[hsl(var(--c-ink-soft))] leading-relaxed">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="wholesale-service-area" className="block-cream border-t-2 border-[hsl(var(--c-ink))]">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-10 lg:px-16 py-20 lg:py-28">
          <Reveal>
            <SectionHeader
              eyebrow="Service Area"
              size="xl"
              title={<>From Palm Beach <br className="hidden sm:block" /> to the rest of America.</>}
              subtitle="Pickup in West Palm Beach. Local and regional delivery across South Florida. Bulk orders coordinated nationwide."
            />
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-0 border-2 border-[hsl(var(--c-ink))]">
            {SERVICE_AREAS.map((a, i) => {
              const Icon = AREA_ICONS[a.icon] || MapPin;
              const accents = ['bg-[hsl(var(--c-mango-yellow))]', 'bg-[hsl(var(--c-mango-orange))]', 'bg-[hsl(var(--c-mango-red))]'];
              return (
                <Reveal key={a.name} delay={i * 0.05}>
                  <div className={`bg-white p-7 sm:p-8 h-full ${i < 2 ? 'md:border-r-2 md:border-[hsl(var(--c-ink))]' : ''} ${i < 2 ? 'border-b-2 md:border-b-0 border-[hsl(var(--c-ink))]' : ''} lift-on-hover`} data-testid={`wholesale-area-${a.name.toLowerCase()}`}>
                    <div className={`inline-flex h-12 w-12 items-center justify-center ${accents[i]} border-2 border-[hsl(var(--c-ink))] text-[hsl(var(--c-ink))]`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="mt-5 font-heading text-2xl font-extrabold">{a.name}</h3>
                    <p className="mt-2 text-sm text-[hsl(var(--c-ink-soft))] leading-relaxed">{a.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal>
            <div className="mt-10 bg-white border-2 border-[hsl(var(--c-ink))] p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 bg-[hsl(var(--c-ink))] text-[hsl(var(--c-mango-yellow))] flex items-center justify-center shrink-0">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-heading text-lg font-extrabold">Counties of focus</div>
                  <div className="text-sm text-[hsl(var(--c-ink-soft))] mt-1">Palm Beach • Broward • Miami-Dade — statewide + out-of-state coordination available.</div>
                </div>
              </div>
              <SquareButton href={BRAND.contact.phoneHref} variant="orange" size="lg" testId="wholesale-call-button">
                <Phone className="h-4 w-4 mr-2" /> Schedule a Call
              </SquareButton>
            </div>
          </Reveal>
        </div>
      </section>

      <section id="wholesale-inquiry-form" data-testid="wholesale-inquiry-section" className="block-orange scroll-mt-32 border-t-2 border-[hsl(var(--c-ink))]">
        <div className="mx-auto max-w-[1100px] px-5 sm:px-10 lg:px-16 py-20 lg:py-28">
          <Reveal>
            <SectionHeader
              tone="cream"
              eyebrow="Get a Custom Quote"
              size="xl"
              title="Wholesale inquiry form."
              subtitle="Tell us about your business, target quantity, and timeline. Our team will reply with pricing, availability, and logistics options."
            />
          </Reveal>
          <Reveal variant="scale">
            <div className="mt-12 bg-white border-2 border-[hsl(var(--c-ink))] p-6 sm:p-10">
              <WholesaleInquiryForm />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="block-cream border-t-2 border-[hsl(var(--c-ink))]">
        <div className="mx-auto max-w-[900px] px-5 sm:px-10 lg:px-16 py-20 lg:py-28">
          <Reveal>
            <SectionHeader eyebrow="FAQ" size="xl" title="Wholesale questions, answered." />
          </Reveal>
          <Reveal>
            <Accordion type="single" collapsible className="mt-10 border-2 border-[hsl(var(--c-ink))] bg-white" data-testid="wholesale-faq-accordion">
              {FAQ_ITEMS.map((f, idx) => (
                <AccordionItem key={f.q} value={`item-${idx}`} className={`border-0 ${idx < FAQ_ITEMS.length - 1 ? 'border-b-2 border-[hsl(var(--c-ink))]' : ''}`}>
                  <AccordionTrigger className="px-5 sm:px-6 py-5 text-left font-heading text-base sm:text-xl font-extrabold hover:no-underline" data-testid={`wholesale-faq-trigger-${idx}`}>
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="px-5 sm:px-6 pb-6 text-sm text-[hsl(var(--c-ink-soft))] leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <section className="block-red border-t-2 border-[hsl(var(--c-ink))]">
        <div className="mx-auto max-w-[1400px] grid grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-8 px-5 sm:px-10 lg:px-16 py-20 lg:py-28 lg:border-r-2 lg:border-[hsl(var(--c-ink))]">
            <div className="eyebrow text-white/85">Ready to Source</div>
            <h2 className="mt-3 sm:mt-4 font-heading display-lg font-extrabold text-white leading-[0.95] tracking-[-0.02em]">
              Need wholesale mangoes <br className="hidden sm:block" /> or produce distribution?
            </h2>
          </div>
          <div className="lg:col-span-4 bg-[hsl(var(--c-ink))] p-8 sm:p-10 lg:p-12 flex flex-col justify-center gap-0">
            <SquareButton href="#wholesale-inquiry-form" variant="yellow" size="lg" fullWidth testId="wholesale-footer-pricing">Request Pricing</SquareButton>
            <SquareButton to="/contact?subject=Wholesale+Call" variant="orange" size="lg" fullWidth className="-mt-[2px]" testId="wholesale-footer-call">Schedule a Call</SquareButton>
          </div>
        </div>
      </section>
    </div>
  );
}
